/* mo-brand-lineup.js: behavior for sections/mo-brand-lineup.liquid (2026-10-01).
 * Vanilla JS, no libraries required. The page is complete without this file; it adds the size
 * picker, feel and floor filters, per-size delivery dates, the build drawings and the hero showpiece
 * (the grid, or the cutaway).
 * Reduced motion: no squash, no rolling prices, the showpiece draws one still frame.
 * The chosen store is read from the existing showroom preference (assets/custom.js). */
(function () {
  'use strict';
  if (window.MOLineup) return;

  var ETA_API = 'https://shopify-rv-eta-production.up.railway.app/api/eta/batch';
  var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var flowReady = false;
  var etaById = {};

  function all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function money(cents) { return '$' + Math.round(cents / 100).toLocaleString('en-US'); }
  function keys(value) { return (value || '').split(',').map(function (k) { return k.trim(); }).filter(Boolean); }

  /* ---------- stores (names and coordinates come from the header's data island) ---------- */
  var stores = {};
  function readStores() {
    var island = document.querySelector('[data-mo-showrooms]');
    if (!island) return;
    try {
      (JSON.parse(island.textContent).stores || []).forEach(function (s) { stores[s.key] = s; });
    } catch (e) { /* keep defaults */ }
  }
  function storeName(key) {
    if (stores[key] && stores[key].name) return stores[key].name;
    if (key === 'nicholasville-road') return 'Lexington';
    return key ? key.charAt(0).toUpperCase() + key.slice(1) : '';
  }
  function selectedStore() { return document.documentElement.getAttribute('data-mo-selected-showroom') || ''; }
  function miles(a, b) {
    var rad = Math.PI / 180, dLat = (b.lat - a.lat) * rad, dLon = (b.lon - a.lon) * rad;
    var h = Math.pow(Math.sin(dLat / 2), 2) + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.pow(Math.sin(dLon / 2), 2);
    return 3958.8 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  }
  function nearest(fromKey, candidates) {
    var from = stores[fromKey];
    if (!from) return candidates[0];
    return candidates.slice().sort(function (x, y) {
      return (stores[x] ? miles(from, stores[x]) : 1e9) - (stores[y] ? miles(from, stores[y]) : 1e9);
    })[0];
  }
  function spread(list) {
    if (list.length === 5) return 'at all five showrooms';
    return list.length === 1 ? 'at one showroom' : 'at ' + list.length + ' showrooms';
  }

  /* ---------- build drawing: side view at 3px per inch, top layers in the brand color ---------- */
  function crossSection(row, version) {
    var inches = parseFloat((version && version.getAttribute('data-h')) || row.getAttribute('data-height'));
    if (!inches) return '';
    var layers = keys(row.getAttribute('data-grids')).map(parseFloat).filter(function (n) { return n > 0; });
    var coils = row.getAttribute('data-coils') === 'true';
    var S = 3, W = 64, H = 56, h = Math.min(inches * S, H - 1), top = H - h;
    var out = '<rect class="x-body" x="0.5" y="' + top + '" width="' + (W - 1) + '" height="' + (h - 0.5) + '" rx="2"/>';
    var y = top + 1.5, x;
    layers.forEach(function (g) {
      var gh = g * S;
      out += '<rect class="x-grid" x="1.5" y="' + y + '" width="' + (W - 3) + '" height="' + gh + '"/>';
      for (x = 6; x < W - 2; x += 5) out += '<line class="x-cell" x1="' + x + '" y1="' + y + '" x2="' + x + '" y2="' + (y + gh) + '"/>';
      y += gh + 1.5;
    });
    if (coils) for (x = 6; x < W - 2; x += 5) out += '<line class="x-coil" x1="' + x + '" y1="' + (y + 3) + '" x2="' + x + '" y2="' + (H - 5) + '"/>';
    out += '<line class="x-floor" x1="-4" y1="' + H + '" x2="' + (W + 4) + '" y2="' + H + '"/>';
    return '<svg viewBox="0 0 ' + W + ' ' + H + '" aria-hidden="true" focusable="false">' + out + '</svg>';
  }

  /* ---------- build drawing, cutaway (rows that list their layers) ----------
   * A side view with the cover peeled back: the finished border on the left, the build on the
   * right. Total height is to scale, 5 units per inch, so rows compare down the page. The split
   * between layers is illustrative: makers publish the order of the layers, not always their
   * depths. "See the layers" lifts the layers apart. */
  var cutCount = 0;
  function cutaway(inches, kinds) {
    var VW = 204, VH = 112, X0 = 14, X1 = 198, FLOOR = 103, BASE = 5, COIL = 36;
    var h = Math.max(52, Math.min(inches * 5, 90)), top = FLOOR - h, id = 'mo-cut-' + (++cutCount);
    var CUT = X0 + (X1 - X0) * 0.36;
    var lid = 'quilt', comfort = [], coil = 'nested', edge = false, vents = 0, velvet = false;
    kinds.forEach(function (k) {
      if (/^(quilt|pillow)/.test(k)) lid = k;
      else if (k === 'foam' || k === 'micro') comfort.push(k);
      else if (/^(wrapped|capped|nested|nested-hd)$/.test(k)) coil = k;
      else if (k === 'edge') edge = true;
      else if (/^vents/.test(k)) vents = k === 'vents8' ? 2 : 1;
      else if (k === 'velvet') velvet = true;
    });
    if (!comfort.length) comfort.push('foam');
    var pillow = /^pillow/.test(lid), tufted = /tufted$/.test(lid), loose = lid === 'pillow-loose';
    var coilBottom = FLOOR - BASE, coilTop = coilBottom - COIL;
    var lidH = pillow ? 12 : 6, seam = pillow ? (loose ? 3.2 : 1.4) : 0, bodyTop = top + lidH + seam;
    var weight = { foam: 1.4, micro: 0.8 }, total = 0, i, x;
    comfort.forEach(function (k) { total += weight[k]; });
    function n(v) { return Math.round(v * 10) / 10; }
    function cx(y) { return CUT + (y - top) * 0.2; }
    /* a spring seen from the side: a chain of arcs about a center line */
    function spring(c, y0, y1, amp, turns) {
      var d = 'M' + n(c) + ',' + n(y0), p = (y1 - y0) / (turns * 2), y = y0, dir = 1, j;
      for (j = 0; j < turns * 2; j++) { d += 'Q' + n(c + dir * amp * 2) + ',' + n(y + p / 2) + ' ' + n(c) + ',' + n(y + p); y += p; dir = -dir; }
      return d;
    }
    /* a quilted edge: shallow puffs between xa and xb; the valleys are where tufts go */
    function puffs(xa, xb, y, amp) {
      var count = Math.max(2, Math.round((xb - xa) / 15)), step = (xb - xa) / count, d = 'M' + n(xa) + ',' + n(y), valleys = [], j;
      for (j = 0; j < count; j++) {
        d += 'Q' + n(xa + step * (j + 0.5)) + ',' + n(y - amp) + ' ' + n(xa + step * (j + 1)) + ',' + n(y);
        if (j < count - 1) valleys.push(xa + step * (j + 1));
      }
      return { d: d, valleys: valleys };
    }
    var face = 'M' + n(cx(bodyTop)) + ',' + n(bodyTop) + 'H' + X1 + 'V' + (FLOOR - 3) + 'Q' + X1 + ',' + FLOOR + ' ' + (X1 - 3) + ',' + FLOOR + 'H' + n(cx(FLOOR)) + 'Z';

    var o = '<defs>' +
      '<clipPath id="' + id + 'p"><path d="M' + n(cx(-30)) + ',-30H' + (VW + 20) + 'V' + (VH + 20) + 'H' + n(cx(VH + 20)) + 'Z"/></clipPath>' +
      '<clipPath id="' + id + 'c"><path d="' + face + '"/></clipPath></defs>';
    o += '<ellipse class="c-shadow" cx="' + ((X0 + X1) / 2) + '" cy="' + (FLOOR + 2.6) + '" rx="' + ((X1 - X0) / 2 + 5) + '" ry="2.4"/>';
    o += '<path class="c-dim" d="M6,' + n(top) + 'V' + FLOOR + 'M3.2,' + n(top) + 'h5.6M3.2,' + FLOOR + 'h5.6"/>';

    /* the cut face, then what never moves: base and coils */
    o += '<path class="c-cut" d="' + face + '"/>';
    o += '<g clip-path="url(#' + id + 'c)"><rect class="c-fab" x="' + X0 + '" y="' + coilBottom + '" width="' + (X1 - X0) + '" height="' + BASE + '"/>';
    var count = coil === 'nested-hd' ? 11 : 9, xs = cx(coilBottom) + 4, xe = X1 - (edge ? 9 : 4), pitch = (xe - xs) / count, amp = pitch * 0.36, c;
    for (i = 0; i < count; i++) {
      c = xs + pitch * (i + 0.5);
      if (coil === 'wrapped' || coil === 'capped') {
        var capped = coil === 'capped', pt = coilTop + (capped ? 6.4 : 1);
        o += '<rect class="c-pocket" x="' + n(c - pitch * 0.44) + '" y="' + n(pt) + '" width="' + n(pitch * 0.88) + '" height="' + n(coilBottom - pt - 0.6) + '" rx="2.2"/>';
        o += '<path class="c-coil" d="' + spring(c, pt + 2, coilBottom - 2, amp * 0.8, 5) + '"/>';
        if (capped) o += '<rect class="c-foam" x="' + n(c - pitch * 0.42) + '" y="' + n(coilTop + 0.9) + '" width="' + n(pitch * 0.84) + '" height="4.8" rx="1.4"/>';
      } else {
        o += '<path class="c-coil" d="' + spring(c, coilTop + 1.5, coilBottom - 1, amp, 5) + '"/>';
        o += '<path class="c-coil2" d="' + spring(c, coilTop + COIL * 0.36, coilBottom - 1, amp * 0.5, 4) + '"/>';
      }
    }
    if (edge) o += '<path class="c-edge" d="' + spring(X1 - 4.6, coilTop + 1.5, coilBottom - 1, 2.2, 9) + '"/>';
    o += '</g>';

    /* comfort layers, clipped to the peeled side so they lift inside the cut */
    o += '<g clip-path="url(#' + id + 'p)">';
    var y = bodyTop, room = coilTop - bodyTop;
    comfort.forEach(function (k, idx) {
      var hh = room * weight[k] / total;
      o += '<g class="c-lift" style="--lift:' + 5 * (comfort.length - idx) + '">';
      if (k === 'micro') {
        o += '<rect class="c-cut" x="' + X0 + '" y="' + n(y) + '" width="' + (X1 - X0 - 1.2) + '" height="' + n(hh - 0.9) + '"/>';
        var z = 'M' + n(cx(y) + 1) + ',' + n(y + hh / 2 - 0.4), zx, up = 1, za = (hh - 2.6) / 2;
        for (zx = cx(y) + 1; zx < X1 - 3; zx += 2.2, up = -up) z += 'Q' + n(zx + 1.1) + ',' + n(y + hh / 2 - 0.4 - up * za * 2) + ' ' + n(zx + 2.2) + ',' + n(y + hh / 2 - 0.4);
        o += '<path class="c-micro" d="' + z + '"/>';
      } else {
        o += '<rect class="c-foam" x="' + X0 + '" y="' + n(y) + '" width="' + (X1 - X0 - 1.2) + '" height="' + n(hh - 0.9) + '"/>';
        o += '<path class="c-hl" d="M' + X0 + ',' + n(y + 1.1) + 'H' + (X1 - 2) + '"/>';
      }
      o += '</g>';
      y += hh;
    });
    o += '</g>';

    /* the far side of the cover, then the finished border: channels, piping, label, vents */
    o += '<path class="c-fabline" d="M' + X1 + ',' + n(bodyTop) + 'V' + (FLOOR - 3) + 'Q' + X1 + ',' + FLOOR + ' ' + (X1 - 3) + ',' + FLOOR + '"/>';
    o += '<path class="c-fab" d="M' + (X0 + 4) + ',' + n(bodyTop) + 'H' + n(cx(bodyTop)) + 'L' + n(cx(FLOOR)) + ',' + FLOOR + 'H' + (X0 + 4) + 'Q' + X0 + ',' + FLOOR + ' ' + X0 + ',' + (FLOOR - 4) + 'V' + n(bodyTop + 4) + 'Q' + X0 + ',' + n(bodyTop) + ' ' + (X0 + 4) + ',' + n(bodyTop) + 'Z"/>';
    var mid = (bodyTop + FLOOR) / 2;
    if (velvet) {
      var v1 = mid + 9, v2 = FLOOR - 4.2;
      o += '<path class="c-velvet" d="M' + X0 + ',' + n(v1) + 'H' + n(cx(v1)) + 'L' + n(cx(v2)) + ',' + n(v2) + 'H' + X0 + 'Z"/><path class="c-pipe" d="M' + X0 + ',' + n(v1) + 'H' + n(cx(v1)) + '"/>';
    }
    for (x = X0 + 6.5; x < cx(FLOOR) - 4; x += 6.5) {
      /* a channel near the cut starts where the cover begins, so it never runs past the cut */
      var chanTop = Math.max(bodyTop + 2.8, top + (x - CUT + 3.4) / 0.2);
      if (chanTop < FLOOR - 7) o += '<path class="c-chan" d="M' + n(x) + ',' + n(chanTop) + 'V' + (FLOOR - 2.8) + '"/>';
    }
    o += '<path class="c-pipe" d="M' + (X0 + 2) + ',' + n(bodyTop + 1) + 'H' + n(cx(bodyTop + 1) - 0.5) + '"/>';
    o += '<rect class="c-label" x="' + (X0 + 17) + '" y="' + n(mid - 6) + '" width="16" height="11" rx="1.3"/>';
    o += '<path class="c-sig" d="M' + (X0 + 20) + ',' + n(mid + 1.2) + 'c1.6,-3.6 2.7,-3.6 2.8,-0.9c0.1,2.1 1.5,-2.7 2.8,-2.1c1.4,0.7 -0.6,2.7 1.3,2.3c1.4,-0.3 1.7,-1.7 3.2,-1.4"/>';
    if (vents) o += '<circle class="c-vent" cx="' + (X0 + 9.6) + '" cy="' + (FLOOR - 9.4) + '" r="1.6"/>';
    if (vents > 1) o += '<circle class="c-vent" cx="' + (X0 + 42) + '" cy="' + (FLOOR - 9.4) + '" r="1.6"/>';
    o += '<path class="c-stitch" d="M' + n(cx(bodyTop) - 2) + ',' + n(bodyTop + 0.5) + 'L' + n(cx(FLOOR) - 2) + ',' + (FLOOR - 0.5) + '"/>';

    /* the lid: a quilted top panel, or a pillow top with its own border */
    var yT = top, yB = top + lidH, edgeLine, cutT = cx(yT), cutB = cx(yB);
    o += '<g class="c-lift" style="--lift:' + (7 + 4.5 * comfort.length) + '">';
    if (pillow) {
      var xL = X0 - 1.6, xR = X1 + 1.4, r = 5;
      edgeLine = puffs(cutT + 1, xR - r, yT + 1.3, 1.5);
      var cushion = edgeLine.d + 'Q' + n(xR) + ',' + n(yT + 1.3) + ' ' + n(xR) + ',' + n(yT + r + 1) + 'V' + n(yB - r) + 'Q' + n(xR) + ',' + n(yB) + ' ' + n(xR - r) + ',' + n(yB) + 'H' + n(cutB);
      o += '<path class="c-quilt" d="' + cushion + 'L' + n(cutT + 1) + ',' + n(yT + 1.3) + 'Z"/>';
      o += '<path class="c-quiltline" d="M' + n(cx((yT + yB) / 2) + 3) + ',' + n((yT + yB) / 2 + 0.6) + 'H' + n(xR - 4) + '"/>';
      o += '<path class="c-fabline" d="' + cushion + '"/>';
      o += '<path class="c-fab" d="M' + n(xL + r) + ',' + n(yT) + 'H' + n(cutT + 1) + 'L' + n(cutB) + ',' + n(yB) + 'H' + n(xL + r) + 'Q' + n(xL) + ',' + n(yB) + ' ' + n(xL) + ',' + n(yB - r) + 'V' + n(yT + r) + 'Q' + n(xL) + ',' + n(yT) + ' ' + n(xL + r) + ',' + n(yT) + 'Z"/>';
      o += '<path class="c-pipe" d="M' + n(xL + 3) + ',' + n(yT + 1.7) + 'H' + n(cx(yT + 1.7)) + 'M' + n(xL + 3) + ',' + n(yB - 1.7) + 'H' + n(cx(yB - 1.7) - 0.6) + '"/>';
    } else {
      edgeLine = puffs(cutT + 1, X1 - 1, yT + 1.4, 1.3);
      o += '<path class="c-quilt" d="' + edgeLine.d + 'V' + n(yB) + 'H' + n(cutB) + 'L' + n(cutT + 1) + ',' + n(yT + 1.4) + 'Z"/>';
      o += '<path class="c-fabline" d="' + edgeLine.d + 'V' + n(yB) + '"/>';
      o += '<path class="c-fab" d="M' + (X0 + 4) + ',' + n(yT) + 'H' + n(cutT + 1) + 'L' + n(cutB) + ',' + n(yB) + 'H' + X0 + 'V' + n(yT + 4) + 'Q' + X0 + ',' + n(yT) + ' ' + (X0 + 4) + ',' + n(yT) + 'Z"/>';
      o += '<path class="c-pipe" d="M' + (X0 + 1.5) + ',' + n(yB - 0.9) + 'H' + n(cutB - 0.6) + '"/>';
    }
    if (tufted) edgeLine.valleys.forEach(function (vx) {
      o += '<path class="c-tuftline" d="M' + n(vx) + ',' + n(yT + 1.6) + 'v' + (pillow ? 6.5 : 3.2) + '"/><circle class="c-tuft" cx="' + n(vx) + '" cy="' + n(yT + 1.7) + '" r="1.15"/>';
    });
    o += '</g>';
    o += '<path class="c-floor" d="M2,' + (FLOOR + 0.6) + 'H' + (VW - 1) + '"/>';
    return '<svg viewBox="0 0 ' + VW + ' ' + VH + '" aria-hidden="true" focusable="false">' + o + '</svg>';
  }

  /* ---------- one lineup ---------- */
  function Lineup(root) {
    var state = { size: root.getAttribute('data-default-size') || 'Queen', feel: 'all', floorOnly: false };
    var drawName = root.getAttribute('data-draw');
    var drawer = drawName && window.MOLineupDraw && typeof window.MOLineupDraw[drawName] === 'function' ? window.MOLineupDraw[drawName] : null;
    var rows = all('[data-row]', root).map(function (el) {
      var variants = all('.mo-lineup__v', el).map(function (v) {
        var sizes = {};
        try { sizes = JSON.parse(v.querySelector('[data-sizes-json]').textContent); } catch (e) { /* no sizes */ }
        return { el: v, key: v.getAttribute('data-key'), feel: v.getAttribute('data-feel'), floor: keys(v.getAttribute('data-floor')), sizes: sizes };
      });
      variants.forEach(function (v) {
        var slot = v.el.querySelector('[data-xsec]');
        if (!slot) return;
        var kinds = slot.getAttribute('data-kinds');
        var inches = parseFloat(v.el.getAttribute('data-h') || el.getAttribute('data-height')) || 14;
        /* a brand layer can register its own row drawing (window.MOLineupDraw[name]) */
        slot.innerHTML = kinds ? (drawer ? drawer(v.el, inches, keys(kinds)) : cutaway(inches, keys(kinds))) : crossSection(el, v.el);
      });
      return { el: el, variants: variants, picked: el.getAttribute('data-start') || null };
    });

    function onFloor(v) {
      var store = selectedStore();
      return v.floor.length > 0 && (!store || v.floor.indexOf(store) !== -1);
    }

    function setText(el, text) { if (el && el.textContent !== text) el.textContent = text; }

    function setPrice(host, entry) {
      if (!entry || !flowReady || still) { setText(host, entry ? money(entry.price) : 'Not made in ' + state.size); return; }
      var flow = host.querySelector('number-flow');
      if (!flow) {
        host.textContent = '';
        flow = document.createElement('number-flow');
        host.appendChild(flow);
        flow.format = { style: 'currency', currency: 'USD', maximumFractionDigits: 0 };
        flow.transformTiming = { duration: 550, easing: 'cubic-bezier(.2,.9,.3,1)' };
      }
      flow.update(Math.round(entry.price / 100));
    }

    function floorWording(row, v) {
      var store = selectedStore();
      var line = v.el.querySelector('[data-mo-floor-line]');
      if (line) {
        var label = line.querySelector('[data-mo-card-tryability-label]');
        var wrap = line.parentElement;
        if (!store || !stores[store]) {
          setText(label, 'On the floor ' + spread(v.floor));
          wrap.classList.add('is-yes');
        } else if (v.floor.indexOf(store) !== -1) {
          setText(label, 'On the floor in ' + storeName(store));
          if (stores[store].url && line.getAttribute('href') !== stores[store].url) line.setAttribute('href', stores[store].url);
          wrap.classList.add('is-yes');
        } else {
          var near = nearest(store, v.floor);
          setText(label, 'Not on the floor in ' + storeName(store) + '. Nearest is ' + storeName(near) + '.');
          if (stores[near] && stores[near].url && line.getAttribute('href') !== stores[near].url) line.setAttribute('href', stores[near].url);
          wrap.classList.remove('is-yes');
        }
      }
      var other = v.el.querySelector('[data-floor-other]');
      if (other) {
        var otherKeys = keys(other.getAttribute('data-other-keys'));
        var where = store && otherKeys.indexOf(store) !== -1 ? 'in ' + storeName(store) : spread(otherKeys);
        setText(other, other.getAttribute('data-this-label') + " isn't on the floor. " + other.getAttribute('data-other-label') + ' is, ' + where + '.');
      }
    }

    function update() {
      var store = selectedStore(), shown = 0;
      rows.forEach(function (row) {
        var options = row.variants.filter(function (v) {
          if (state.feel !== 'all' && v.feel !== state.feel) return false;
          if (state.floorOnly && !onFloor(v)) return false;
          return true;
        });
        row.el.hidden = !options.length;
        if (!options.length) return;
        shown++;
        var active = options.filter(function (v) { return v.key === row.picked; })[0] ||
          options.filter(onFloor)[0] || options[0];
        row.variants.forEach(function (v) { v.el.hidden = v !== active; });

        var entry = active.sizes[state.size];
        var buy = active.el.querySelector('[data-buy]');
        buy.classList.toggle('is-none', !entry);
        setPrice(buy.querySelector('[data-now]'), entry);
        var onSale = entry && entry.compare > entry.price;
        setText(buy.querySelector('[data-was]'), onSale ? money(entry.compare) : '');
        setText(buy.querySelector('[data-save]'), onSale ? money(entry.compare - entry.price) + ' off' : '');
        setText(buy.querySelector('[data-for]'), entry ? state.size : '');

        var eta = entry && entry.ok ? etaById[String(entry.id)] : null;
        var etaEl = buy.querySelector('[data-eta]');
        var show = !!(eta && eta.displayable && eta.visible_label);
        if (etaEl.hidden === show) etaEl.hidden = !show;
        setText(etaEl.querySelector('[data-eta-text]'), show ? String(eta.visible_label) : '');

        floorWording(row, active);
      });

      all('[data-family]', root).forEach(function (f) { f.hidden = !f.querySelector('[data-row]:not([hidden])'); });
      all('[data-index-from]', root).forEach(function (el) {
        var family = document.getElementById(el.getAttribute('data-index-from')), low = 0;
        if (!family) return;
        rows.forEach(function (row) {
          if (!family.contains(row.el)) return;
          row.variants.forEach(function (v) { var e = v.sizes[state.size]; if (e && (!low || e.price < low)) low = e.price; });
        });
        setText(el, low ? 'from ' + money(low) : '');
      });
      all('[data-price-head]', root).forEach(function (h) { setText(h, 'Price for ' + state.size); });
      all('[data-sizes] button', root).forEach(function (b) { b.setAttribute('aria-pressed', String(b.getAttribute('data-size') === state.size)); });
      all('[data-feels] button', root).forEach(function (b) { b.setAttribute('aria-pressed', String(b.getAttribute('data-feel') === state.feel)); });
      var toggle = root.querySelector('[data-floor-only]');
      if (toggle) {
        toggle.setAttribute('aria-pressed', String(state.floorOnly));
        setText(toggle.querySelector('[data-floor-only-label]'), store && stores[store] ? 'On the floor in ' + storeName(store) : 'On showroom floors');
      }
      setText(root.querySelector('[data-count]'), shown + (shown === 1 ? ' mattress shown' : ' mattresses shown'));
      placeCursors();
    }

    /* slide each pill to the pressed option; the first placement is instant */
    function placeCursors() {
      all('.mo-lineup__seg, .mo-lineup__pick', root).forEach(function (group) {
        var on = group.querySelector('[aria-pressed="true"]');
        var cursor = group.querySelector('.mo-lineup__cursor');
        if (!on || !cursor || !on.offsetWidth) return;
        if (!cursor.hasAttribute('data-placed')) {
          cursor.setAttribute('data-placed', '');
          cursor.style.transition = 'none';
          requestAnimationFrame(function () { requestAnimationFrame(function () { cursor.style.transition = ''; }); });
        }
        cursor.style.width = on.offsetWidth + 'px';
        cursor.style.transform = 'translateX(' + on.offsetLeft + 'px)';
      });
    }

    root.addEventListener('click', function (e) {
      var size = e.target.closest('[data-size]');
      if (size) { state.size = size.getAttribute('data-size'); update(); return; }
      var feel = e.target.closest('[data-feel]');
      if (feel && feel.closest('[data-feels]')) { state.feel = feel.getAttribute('data-feel'); update(); return; }
      if (e.target.closest('[data-floor-only]')) { state.floorOnly = !state.floorOnly; update(); return; }
      var layers = e.target.closest('[data-layers-toggle]');
      if (layers) {
        /* one state per row, so the list stays open when the shopper switches Soft, Medium or Firm */
        var rowBox = layers.closest('[data-row]'), wasOpen = layers.closest('.mo-lineup__build').querySelector('button[data-layers-toggle]').getAttribute('aria-expanded') === 'true';
        all('.mo-lineup__build--cut', rowBox).forEach(function (build) {
          var button = build.querySelector('button[data-layers-toggle]');
          button.setAttribute('aria-expanded', String(!wasOpen));
          build.querySelector('.mo-lineup__layers').hidden = wasOpen;
          build.querySelector('[data-xsec]').classList.toggle('is-open', !wasOpen);
          setText(button.querySelector('[data-layers-label]'), wasOpen ? 'See the layers' : 'Hide the layers');
        });
        return;
      }
      var pick = e.target.closest('[data-pick]');
      if (pick) {
        var rowEl = pick.closest('[data-row]');
        var row = rows.filter(function (r) { return r.el === rowEl; })[0];
        var chosen = row.variants[+pick.getAttribute('data-pick')];
        if (!chosen) return;
        row.picked = chosen.key;
        if (state.feel !== 'all' && chosen.feel !== state.feel) state.feel = 'all';
        if (state.floorOnly && !onFloor(chosen)) state.floorOnly = false;
        update();
        var again = chosen.el.querySelector('[data-pick][aria-pressed="true"]');
        if (again) again.focus({ preventScroll: true });
      }
    });

    /* delivery dates: one batch call for every purchasable size on the page; silent on failure */
    function loadEta() {
      var ids = [];
      rows.forEach(function (row) {
        row.variants.forEach(function (v) {
          Object.keys(v.sizes).forEach(function (s) { if (v.sizes[s].ok) ids.push(String(v.sizes[s].id)); });
        });
      });
      if (!ids.length || !window.fetch) return;
      var controller = 'AbortController' in window ? new AbortController() : null;
      var timer = controller ? setTimeout(function () { controller.abort(); }, 6000) : null;
      fetch(ETA_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: ids.map(function (id) { return { shopify_variant_id: id }; }) }),
        signal: controller ? controller.signal : undefined
      }).then(function (res) { if (!res.ok) throw new Error('eta ' + res.status); return res.json(); })
        .then(function (data) {
          if (timer) clearTimeout(timer);
          (data && data.results || []).forEach(function (r) { if (r && r.shopify_variant_id != null) etaById[String(r.shopify_variant_id)] = r; });
          update();
        }).catch(function () { if (timer) clearTimeout(timer); });
    }

    root.classList.add('is-live');
    update();
    loadEta();
    return { update: update, placeCursors: placeCursors };
  }

  /* ---------- accent word: split into letters so each one lands and squashes once ---------- */
  function squash(root) {
    var word = root.querySelector('[data-mo-squash]');
    if (!word || still || word.querySelector('i')) return;
    // The heading keeps its full text as its accessible name, so the per-letter spans are read as
    // one word and the heading text appears once in the page for crawlers.
    var heading = word.closest('h1, h2, h3');
    if (heading && !heading.hasAttribute('aria-label')) heading.setAttribute('aria-label', heading.textContent.replace(/\s+/g, ' ').trim());
    var text = word.textContent;
    word.textContent = '';
    text.split('').forEach(function (ch, i) {
      var letter = document.createElement('i');
      letter.style.setProperty('--i', i);
      letter.style.setProperty('--r', (Math.random() * 20 - 10).toFixed(1) + 'deg');
      letter.textContent = ch;
      word.appendChild(letter);
    });
  }

  /* ---------- hero grid ----------
   * A plain-canvas port of "Kinetic Grid" by satoriui (21st.dev): the mesh pulls in around the
   * pointer and ripples on release. Here it is sized to the hero, takes the section's colors,
   * presses deeper while the pointer is held, presses once on load, and stops drawing when idle. */
  function heroGrid(root) {
    var hero = root.querySelector('[data-mo-lineup-hero]');
    var canvas = hero && hero.querySelector('canvas');
    if (!canvas || !canvas.getContext) return function () {};
    var ctx = canvas.getContext('2d');
    var CELL = 46, RADIUS = 240, MAX_WARP = 22, LERP = 0.1;
    var mouse = { x: 0, y: 0 }, target = { x: 0, y: 0 }, ripples = [];
    var W = 0, H = 0, ink = [27, 28, 29], accent = [95, 37, 159];
    var presence = 0, presenceTo = 0, press = 0, pressTo = 0, raf = 0, onScreen = true, touched = false;

    function mix(a, b, t) { return a + (b - a) * t; }
    function rgb(value, fallback) {
      ctx.fillStyle = '#000';
      ctx.fillStyle = (value || '').trim();
      var m = /^#([0-9a-f]{6})$/i.exec(ctx.fillStyle);
      if (!m) return fallback;
      var n = parseInt(m[1], 16);
      return [n >> 16 & 255, n >> 8 & 255, n & 255];
    }
    function tint(t, aBase, aActive) {
      return 'rgba(' + ink.map(function (c, i) { return Math.round(mix(c, accent[i], t)); }).join(',') + ',' + mix(aBase, aActive, t).toFixed(3) + ')';
    }
    function readColors() {
      var css = getComputedStyle(root);
      ink = rgb(css.getPropertyValue('--l-ink'), ink);
      accent = rgb(css.getPropertyValue('--l-grid'), accent);
    }
    function resize() {
      var box = canvas.getBoundingClientRect(), dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = box.width; H = box.height;
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    function warp(gx, gy, col, row, cols, rowsN, now) {
      var edge = 1.5;
      var cp = Math.min(col / edge, (cols - 1 - col) / edge, 1), rp = Math.min(row / edge, (rowsN - 1 - row) / edge, 1);
      var pin = cp * cp * rp * rp;
      var dx = gx - mouse.x, dy = gy - mouse.y, dist = Math.sqrt(dx * dx + dy * dy);
      var near = Math.max(0, 1 - dist / RADIUS) * pin * presence;
      var rx = 0, ry = 0, i, r, age, radius, fade, ex, ey, diff, strength, angle;
      for (i = 0; i < ripples.length; i++) {
        r = ripples[i];
        age = Math.max(0, (now - r.born) / 1000); radius = age * 400; fade = Math.max(0, 1 - age * 1.2);
        ex = gx - r.x; ey = gy - r.y;
        diff = Math.sqrt(ex * ex + ey * ey) - radius;
        if (Math.abs(diff) < 55) {
          strength = (1 - Math.abs(diff) / 55) * fade * 18 * pin * (diff < 0 ? 1 : -1);
          angle = Math.atan2(ey, ex);
          rx += Math.cos(angle) * strength; ry += Math.sin(angle) * strength;
        }
      }
      if (dist < RADIUS && dist > 0 && pin > 0) {
        var t = dist / RADIUS;
        var amount = (1 - t) * (1 - t) * Math.min(1, dist / 60) * MAX_WARP * pin * presence * (1 + press * 1.3);
        angle = Math.atan2(dy, dx);
        return [gx - Math.cos(angle) * amount + rx, gy - Math.sin(angle) * amount + ry, near];
      }
      return [gx + rx, gy + ry, near];
    }
    function seg(a, b) {
      var avg = (a[2] + b[2]) / 2, t = avg * avg * (3 - 2 * avg);
      ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]);
      ctx.strokeStyle = tint(t, 0.16, 0.9); ctx.lineWidth = mix(0.8, 1.6, t); ctx.stroke();
    }
    function draw(now) {
      ctx.clearRect(0, 0, W, H);
      var i, row, col;
      for (i = ripples.length - 1; i >= 0; i--) if ((now - ripples[i].born) / 1000 * 1.2 >= 1) ripples.splice(i, 1);
      var cols = Math.max(2, Math.ceil(W / CELL)) + 1, rowsN = Math.max(2, Math.ceil(H / CELL)) + 1;
      var cw = W / (cols - 1), ch = H / (rowsN - 1), pts = [];
      for (row = 0; row < rowsN; row++) {
        pts[row] = [];
        for (col = 0; col < cols; col++) pts[row][col] = warp(col * cw, row * ch, col, row, cols, rowsN, now);
      }
      for (row = 0; row < rowsN; row++) for (col = 0; col < cols - 1; col++) seg(pts[row][col], pts[row][col + 1]);
      for (col = 0; col < cols; col++) for (row = 0; row < rowsN - 1; row++) seg(pts[row][col], pts[row + 1][col]);
      for (row = 0; row < rowsN; row++) {
        for (col = 0; col < cols; col++) {
          var p = pts[row][col], t = p[2] * p[2] * (3 - 2 * p[2]), r = mix(1.6, 3.2, t);
          if (t > 0.3) {
            var halo = r + mix(0, 6, (t - 0.3) / 0.7), g = ctx.createRadialGradient(p[0], p[1], r * 0.5, p[0], p[1], halo);
            g.addColorStop(0, 'rgba(' + accent.join(',') + ',' + (t * 0.3).toFixed(3) + ')');
            g.addColorStop(1, 'rgba(' + accent.join(',') + ',0)');
            ctx.beginPath(); ctx.arc(p[0], p[1], halo, 0, Math.PI * 2); ctx.fillStyle = g; ctx.fill();
          }
          ctx.beginPath(); ctx.arc(p[0], p[1], r, 0, Math.PI * 2); ctx.fillStyle = tint(t, 0.24, 1); ctx.fill();
        }
      }
      for (i = 0; i < ripples.length; i++) {
        var age = Math.max(0, (now - ripples[i].born) / 1000);
        ctx.beginPath(); ctx.arc(ripples[i].x, ripples[i].y, age * 400, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(' + accent.join(',') + ',' + (Math.max(0, 1 - age * 1.2) * 0.3).toFixed(3) + ')';
        ctx.lineWidth = 1.5; ctx.stroke();
      }
    }
    function frame(now) {
      mouse.x = mix(mouse.x, target.x, LERP); mouse.y = mix(mouse.y, target.y, LERP);
      presence = mix(presence, presenceTo, 0.09); press = mix(press, pressTo, 0.14);
      try { draw(now); } catch (e) { raf = 0; return; }
      var dxm = mouse.x - target.x, dym = mouse.y - target.y;
      var settled = Math.abs(presence - presenceTo) < 0.004 && Math.abs(press - pressTo) < 0.004 && Math.sqrt(dxm * dxm + dym * dym) < 0.3 && !ripples.length;
      raf = onScreen && !settled ? requestAnimationFrame(frame) : 0;
    }
    function wake() { if (!raf && onScreen && !still) raf = requestAnimationFrame(frame); }
    function aim(e) {
      var box = canvas.getBoundingClientRect();
      target.x = e.clientX - box.left; target.y = e.clientY - box.top;
      if (presence < 0.05) { mouse.x = target.x; mouse.y = target.y; }
      touched = true; presenceTo = 1; wake();
    }
    function release() {
      if (pressTo) ripples.push({ x: target.x, y: target.y, born: performance.now() });
      pressTo = 0; wake();
    }
    function letGo() { release(); presenceTo = 0; wake(); }
    function repaint() {
      readColors(); resize();
      if (still) { mouse.x = W * 0.74; mouse.y = H * 0.5; presence = 1; press = 0.5; draw(0); } else { draw(performance.now()); wake(); }
    }

    repaint();
    if (!still) {
      hero.addEventListener('pointermove', aim);
      hero.addEventListener('pointerdown', function (e) { aim(e); pressTo = 1; });
      hero.addEventListener('pointerup', function (e) { release(); if (e.pointerType !== 'mouse') presenceTo = 0; });
      hero.addEventListener('pointerleave', letGo);
      hero.addEventListener('pointercancel', letGo);
      if ('IntersectionObserver' in window) new IntersectionObserver(function (es) { onScreen = es[0].isIntersecting; wake(); }).observe(hero);
      /* one press on load, timed to the accent word landing, to show the grid is live */
      setTimeout(function () {
        if (touched) return;
        var wide = W > 720;
        mouse.x = target.x = W * (wide ? 0.74 : 0.5); mouse.y = target.y = H * (wide ? 0.5 : 0.8);
        presenceTo = 1; pressTo = 1; wake();
        setTimeout(function () {
          if (touched) return;
          release();
          setTimeout(function () { if (!touched) { presenceTo = 0; wake(); } }, 1100);
        }, 800);
      }, 750);
    }
    return repaint;
  }

  /* ---------- hero cutaway ----------
   * One mattress, cut open. The left third keeps its cover: channel-quilted border, piped seam,
   * signed label, air vents. The rest shows the build, and the coils are live. Each column is a
   * spring: the tall outer coil carries a light load alone, and once it has shortened past the
   * gap the short inner coil joins in and the column gets about six times stiffer. Hover is a
   * light load, a held press a heavy one. The spring behavior follows "Spring Element" by
   * skyleen77 (21st.dev), ported to plain canvas.
   * On first view the cover peels back, the label is signed and the bed is pressed once. Numbered
   * pins match the parts list under the drawing. It stops drawing when idle and writes nothing to
   * the page. */
  function heroCutaway(root) {
    var hero = root.querySelector('[data-mo-lineup-hero]');
    var stage = hero && hero.querySelector('.mo-lineup__stage');
    var canvas = stage && stage.querySelector('canvas');
    if (!canvas || !canvas.getContext) return function () {};
    var ctx = canvas.getContext('2d');
    var partEls = all('[data-part]', stage), parts = partEls.map(function (el) { return el.getAttribute('data-part'); });
    var K1 = 170, K2 = 850, DAMP = 11, TIE = 55, HOVER = 2600, PRESS = 16500;
    var W = 0, H = 0, G = null, cols = [], C = {}, SIG = [], TAU = Math.PI * 2;
    var px = 0, pxTo = 0, load = 0, loadTo = 0, raf = 0, last = 0, onScreen = false;
    var touched = false, inside = false, hot = -1, peel = still ? 1 : 0, sign = still ? 1 : 0, introAt = 0, demo = 0;
    for (var q = 0; q <= 48; q++) SIG.push([q / 48, 0.5 + 0.34 * Math.sin(q / 48 * 13.8 + 0.5) * (1 - 0.55 * q / 48) + 0.08 * Math.sin(q / 48 * 34)]);

    function mix(a, b, t) { return a + (b - a) * t; }
    function clamp(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
    function ease(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
    function rgba(c, a) { return 'rgba(' + c[0] + ',' + c[1] + ',' + c[2] + ',' + (a == null ? 1 : a).toFixed(3) + ')'; }
    /* the canvas parses each color, so any CSS color works and nothing is written to the page */
    function rgb(value, fallback) {
      ctx.fillStyle = '#000';
      ctx.fillStyle = (value || '').trim();
      var m = /^#([0-9a-f]{6})$/i.exec(ctx.fillStyle);
      if (!m) return fallback;
      var n = parseInt(m[1], 16);
      return [n >> 16 & 255, n >> 8 & 255, n & 255];
    }
    function readColors() {
      var css = getComputedStyle(stage);
      ['fab', 'fab-hi', 'fab-lo', 'gold', 'gold-ink', 'cut', 'foam', 'quilt', 'coil', 'pin'].forEach(function (k) { C[k] = rgb(css.getPropertyValue('--c-' + k), C[k] || [120, 120, 120]); });
      C.ink = rgb(css.getPropertyValue('--l-ink'), [27, 28, 29]);
    }

    function layout() {
      var box = canvas.getBoundingClientRect(), dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = box.width; H = box.height;
      if (!W || !H) { G = null; return; }
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      var g = {}, m = Math.min(W - 58, 640);
      g.x0 = (W - m) / 2; g.x1 = g.x0 + m;
      g.floor = H - 12;
      g.h = Math.min(H - 12 - 34, m / (m > 480 ? 2.5 : 2.15));
      g.top = g.floor - g.h;
      g.cut = g.x0 + m * (m > 480 ? 0.33 : 0.3);
      g.slant = g.h * 0.2;
      g.r = Math.min(12, g.h * 0.09);
      g.quilt = Math.max(8, g.h * 0.075);
      g.foam = g.h * 0.2;
      g.base = Math.max(6, g.h * 0.055);
      g.coilTop = g.top + g.quilt + g.foam; g.coilBottom = g.floor - g.base;
      g.tall = g.coilBottom - g.coilTop; g.gap = g.tall * 0.3;
      g.edge = g.x1 - Math.max(9, m * 0.022);
      g.puff = m / Math.round(m / 27);
      g.chan = (g.cut - g.x0) / Math.max(4, Math.round((g.cut - g.x0) / 19));
      g.label = Math.max(30, Math.min(54, m * 0.085));
      g.spread = Math.max(46, m * 0.105);
      g.a = g.cut + g.slant + 10; g.b = g.edge - 12;
      var n = Math.max(5, Math.round((g.b - g.a) / (m > 480 ? 31 : 26))), step = (g.b - g.a) / n, old = cols;
      g.radius = step * 0.36;
      cols = [];
      for (var i = 0; i < n; i++) cols.push({ x: g.a + step * (i + 0.5), y: old[i] ? Math.min(old[i].y, g.tall * 0.5) : 0, v: 0 });
      G = g;
    }

    function step(dt) {
      var i, c, f, n = cols.length, l, r, d;
      for (i = 0; i < n; i++) {
        c = cols[i];
        d = c.x - px;
        f = load * Math.exp(-(d * d) / (2 * G.spread * G.spread));
        l = i > 0 ? cols[i - 1].y : c.y; r = i < n - 1 ? cols[i + 1].y : c.y;
        f += TIE * (l + r - 2 * c.y) - K1 * c.y - DAMP * c.v;
        if (c.y > G.gap) f -= K2 * (c.y - G.gap);
        c.a = f;
      }
      for (i = 0; i < n; i++) { c = cols[i]; c.v += c.a * dt; c.y += c.v * dt; if (c.y > G.tall * 0.56) { c.y = G.tall * 0.56; c.v = 0; } }
    }

    /* how far the top surface has sunk at x: follows the columns, and eases out under the cover */
    function sag(x) {
      var n = cols.length, first = cols[0], t;
      if (x <= first.x) { t = Math.max(0, 1 - (first.x - x) / 90); return first.y * t * t * (3 - 2 * t); }
      if (x >= cols[n - 1].x) return cols[n - 1].y;
      var f = (x - first.x) / (cols[1].x - first.x), i = Math.min(n - 2, Math.floor(f)), u = f - i;
      u = u * u * (3 - 2 * u);
      return cols[i].y + (cols[i + 1].y - cols[i].y) * u;
    }
    function topY(x) { return G.top + sag(x) - 2.2 * Math.abs(Math.sin((x - G.x0) * Math.PI / G.puff)); }
    function along(xa, xb, fn, start) {
      var x, d = xa < xb ? 5 : -5;
      for (x = xa; d > 0 ? x < xb : x > xb; x += d) { if (start && x === xa) ctx.moveTo(x, fn(x)); else ctx.lineTo(x, fn(x)); }
      ctx.lineTo(xb, fn(xb));
    }
    function outline() {
      var g = G, r = g.r;
      ctx.beginPath();
      ctx.moveTo(g.x0, g.top + r);
      ctx.quadraticCurveTo(g.x0, g.top - 1, g.x0 + r, topY(g.x0 + r));
      along(g.x0 + r, g.x1 - r, topY);
      ctx.quadraticCurveTo(g.x1, g.top + sag(g.x1) - 1, g.x1, g.top + sag(g.x1) + r);
      ctx.lineTo(g.x1, g.floor - r);
      ctx.quadraticCurveTo(g.x1, g.floor, g.x1 - r, g.floor);
      ctx.lineTo(g.x0 + r, g.floor);
      ctx.quadraticCurveTo(g.x0, g.floor, g.x0, g.floor - r);
      ctx.closePath();
    }
    function cutAt(y, cutX) { return cutX + (y - G.top) / G.h * G.slant; }
    function clipSide(cutX, right) {
      var y0 = G.top - 60, y1 = G.floor + 20;
      ctx.beginPath();
      if (right) { ctx.moveTo(cutAt(y0, cutX), y0); ctx.lineTo(W + 60, y0); ctx.lineTo(W + 60, y1); ctx.lineTo(cutAt(y1, cutX), y1); }
      else { ctx.moveTo(-60, y0); ctx.lineTo(cutAt(y0, cutX), y0); ctx.lineTo(cutAt(y1, cutX), y1); ctx.lineTo(-60, y1); }
      ctx.closePath(); ctx.clip();
    }
    /* one coil seen from the side: the far half of each turn is drawn fainter than the near half */
    function helix(cx, top, bottom, r, turns, color, alpha, width) {
      if (bottom - top < 5) return;
      var steps = turns * 14, i, t, a, pass, open, far, x, y;
      for (pass = 0; pass < 2; pass++) {
        ctx.beginPath(); open = false;
        for (i = 0; i <= steps; i++) {
          t = i / steps; a = t * turns * TAU;
          far = Math.sin(a) < 0; x = cx + Math.cos(a) * r; y = mix(top, bottom, t) + Math.sin(a) * r * 0.3 * (1 - Math.abs(2 * t - 1) * 0.6);
          if ((pass === 0) === far) { if (open) ctx.lineTo(x, y); else { ctx.moveTo(x, y); open = true; } }
          else if (open) { ctx.lineTo(x, y); open = false; }
        }
        ctx.strokeStyle = rgba(color, pass === 0 ? alpha * 0.36 : alpha);
        ctx.lineWidth = width; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.stroke();
      }
    }
    function pin(index, x, y, tx, ty, alpha) {
      if (index < 0 || alpha <= 0) return;
      var on = hot === index;
      ctx.globalAlpha = alpha;
      if (tx != null) {
        ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(tx, ty); ctx.strokeStyle = rgba(C.ink); ctx.lineWidth = 1; ctx.stroke();
        ctx.beginPath(); ctx.arc(tx, ty, 1.9, 0, TAU); ctx.fillStyle = rgba(C.ink); ctx.fill();
      }
      ctx.beginPath(); ctx.arc(x, y, 9.5, 0, TAU); ctx.fillStyle = rgba(on ? C.ink : C.pin); ctx.fill();
      ctx.strokeStyle = rgba(C.ink); ctx.lineWidth = 1.2; ctx.stroke();
      ctx.fillStyle = rgba(on ? C.pin : C.ink); ctx.font = '600 11px Inter, system-ui, sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(String(index + 1), x, y + 0.5);
      ctx.globalAlpha = 1;
    }

    function draw() {
      ctx.clearRect(0, 0, W, H);
      if (!G || !cols.length) return;
      var g = G, e = ease(peel), cutX = mix(g.x1 + 18, g.cut, e), n = cols.length, i, c, x, mid = (g.x0 + g.x1) / 2;
      var y1 = g.top + g.quilt + 3, y2 = g.coilBottom + 1;
      function under(d) { return function (x) { return g.top + sag(x) + d; }; }

      /* floor shadow */
      ctx.save(); ctx.translate(0, g.floor + 3); ctx.scale(1, 0.045); ctx.translate(0, -(g.floor + 3));
      var shade = ctx.createRadialGradient(mid, g.floor + 3, 6, mid, g.floor + 3, (g.x1 - g.x0) * 0.58);
      shade.addColorStop(0, rgba(C.ink, 0.2)); shade.addColorStop(1, rgba(C.ink, 0));
      ctx.fillStyle = shade; ctx.beginPath(); ctx.arc(mid, g.floor + 3, (g.x1 - g.x0) * 0.58, 0, TAU); ctx.fill(); ctx.restore();

      ctx.save(); outline(); ctx.clip();

      /* the build, right of the cut */
      ctx.save(); clipSide(cutX, true);
      ctx.fillStyle = rgba(C.cut); ctx.fillRect(g.x0, g.top - 12, g.x1 - g.x0, g.h + 24);
      ctx.fillStyle = rgba(C.fab); ctx.fillRect(g.x0, g.coilBottom, g.x1 - g.x0, g.base + 2);
      for (i = 0; i < n; i++) {
        c = cols[i];
        var hit = clamp((c.y - g.gap + 5) / 10);
        helix(c.x, g.coilTop + g.gap + Math.max(0, c.y - g.gap) + 3, g.coilBottom - 2, g.radius * 0.46, 5, C['gold-ink'], mix(0.8, 1, hit), mix(1.8, 2.7, hit));
        helix(c.x, g.coilTop + c.y + 3, g.coilBottom - 2, g.radius, 5, C.coil, 0.92, 1.3);
      }
      helix(g.edge, g.coilTop + cols[n - 1].y + 3, g.coilBottom - 2, Math.min(5, g.radius * 0.5), 10, C.ink, 0.85, 1.4);
      ctx.beginPath(); along(g.x0, g.x1, under(g.quilt), true); along(g.x1, g.x0, under(g.quilt + g.foam)); ctx.closePath();
      ctx.fillStyle = rgba(C.foam); ctx.fill();
      ctx.beginPath(); along(g.x0, g.x1, under(g.quilt + 1.5), true); ctx.strokeStyle = 'rgba(255,255,255,.5)'; ctx.lineWidth = 1; ctx.stroke();
      ctx.beginPath(); along(g.x0, g.x1, topY, true); along(g.x1, g.x0, under(g.quilt)); ctx.closePath();
      ctx.fillStyle = rgba(C.quilt); ctx.fill();
      ctx.fillStyle = rgba(C.fab, 0.55);
      for (x = g.x0 + g.puff; x < g.x1 - 4; x += g.puff) { ctx.beginPath(); ctx.arc(x, g.top + sag(x) + 2.6, 1.3, 0, TAU); ctx.fill(); }
      ctx.restore();

      /* the cover, left of the cut */
      ctx.save(); clipSide(cutX, false);
      ctx.fillStyle = rgba(C.fab); ctx.fillRect(g.x0 - 2, g.top - 14, g.x1 - g.x0 + 4, g.h + 28);
      for (x = g.x0; x < Math.min(cutX + g.slant, g.x1); x += g.chan) {
        var ch = ctx.createLinearGradient(x, 0, x + g.chan, 0);
        ch.addColorStop(0, 'rgba(0,0,0,.26)'); ch.addColorStop(0.2, 'rgba(255,255,255,0)'); ch.addColorStop(0.52, 'rgba(255,255,255,.11)'); ch.addColorStop(0.84, 'rgba(255,255,255,0)'); ch.addColorStop(1, 'rgba(0,0,0,.26)');
        ctx.fillStyle = ch; ctx.fillRect(x, y1, g.chan, y2 - y1);
      }
      ctx.beginPath(); along(g.x0, g.x1, under(-8), true); along(g.x1, g.x0, under(g.quilt + 1)); ctx.closePath();
      ctx.fillStyle = rgba(C.fab); ctx.fill();
      ctx.beginPath(); along(g.x0, g.x1, under(g.quilt + 1), true); ctx.strokeStyle = rgba(C.gold); ctx.lineWidth = 1.6; ctx.stroke();
      ctx.fillStyle = rgba(C['fab-lo']); ctx.fillRect(g.x0, y2, g.x1 - g.x0, g.base + 2);
      ctx.beginPath(); ctx.moveTo(g.x0, y2); ctx.lineTo(g.x1, y2); ctx.strokeStyle = rgba(C.gold, 0.5); ctx.lineWidth = 1; ctx.stroke();
      var lw = g.label, lh = lw * 0.7, lx = g.x0 + (g.cut - g.x0) * 0.5 - lw / 2, ly = (y1 + y2) / 2 - lh / 2;
      ctx.beginPath(); ctx.moveTo(lx + 3, ly); ctx.lineTo(lx + lw - 3, ly); ctx.quadraticCurveTo(lx + lw, ly, lx + lw, ly + 3); ctx.lineTo(lx + lw, ly + lh - 3); ctx.quadraticCurveTo(lx + lw, ly + lh, lx + lw - 3, ly + lh); ctx.lineTo(lx + 3, ly + lh); ctx.quadraticCurveTo(lx, ly + lh, lx, ly + lh - 3); ctx.lineTo(lx, ly + 3); ctx.quadraticCurveTo(lx, ly, lx + 3, ly); ctx.closePath();
      ctx.fillStyle = rgba(C['fab-lo']); ctx.fill(); ctx.strokeStyle = rgba(C.gold); ctx.lineWidth = 1; ctx.stroke();
      ctx.beginPath(); ctx.moveTo(lx + lw * 0.22, ly + lh * 0.24); ctx.lineTo(lx + lw * 0.78, ly + lh * 0.24); ctx.strokeStyle = rgba(C.gold, 0.6); ctx.stroke();
      var k = Math.round(sign * (SIG.length - 1));
      if (k > 0) {
        ctx.beginPath();
        for (i = 0; i <= k; i++) { x = lx + lw * (0.16 + SIG[i][0] * 0.68); var sy = ly + lh * (0.36 + SIG[i][1] * 0.5); if (i) ctx.lineTo(x, sy); else ctx.moveTo(x, sy); }
        ctx.strokeStyle = rgba(C.gold); ctx.lineWidth = 1.3; ctx.lineJoin = 'round'; ctx.lineCap = 'round'; ctx.stroke();
      }
      var vy = g.coilBottom - Math.max(10, g.h * 0.075), vents = [g.x0 + 15, g.x0 + 15 + (g.cut - g.x0) * 0.6];
      vents.forEach(function (vx) {
        ctx.beginPath(); ctx.arc(vx, vy, 3.3, 0, TAU); ctx.fillStyle = rgba(C.gold); ctx.fill();
        ctx.beginPath(); ctx.arc(vx, vy, 1.4, 0, TAU); ctx.fillStyle = rgba(C['fab-lo']); ctx.fill();
      });
      ctx.restore();

      /* the cut itself: a shadow on the build, the cover's rolled edge, a row of stitches */
      var roll = 5 + 11 * (1 - e), skew = g.slant / g.h;
      ctx.save(); ctx.transform(1, 0, skew, 1, cutX - skew * g.top, 0);
      var s1 = ctx.createLinearGradient(0, 0, 22, 0); s1.addColorStop(0, rgba(C.ink, 0.22)); s1.addColorStop(1, rgba(C.ink, 0));
      ctx.fillStyle = s1; ctx.fillRect(0, g.top - 20, 22, g.h + 40);
      var s2 = ctx.createLinearGradient(-roll, 0, 0, 0); s2.addColorStop(0, rgba(C.fab)); s2.addColorStop(0.5, rgba(C['fab-hi'])); s2.addColorStop(1, rgba(C['fab-lo']));
      ctx.fillStyle = s2; ctx.fillRect(-roll, g.top - 20, roll, g.h + 40);
      ctx.setLineDash([5, 4]); ctx.beginPath(); ctx.moveTo(-roll - 5, g.top - 20); ctx.lineTo(-roll - 5, g.floor + 20);
      ctx.strokeStyle = rgba(C.gold); ctx.lineWidth = 1.4; ctx.stroke(); ctx.setLineDash([]);
      ctx.restore();
      ctx.restore();

      outline(); ctx.strokeStyle = rgba(C.fab); ctx.lineWidth = 1.8; ctx.stroke();
      ctx.beginPath(); ctx.moveTo(g.x0 - 14, g.floor + 0.6); ctx.lineTo(g.x1 + 14, g.floor + 0.6); ctx.strokeStyle = rgba(C.ink); ctx.lineWidth = 1.2; ctx.stroke();

      /* numbered pins, shown once the cover is back */
      var show = clamp((peel - 0.82) / 0.18), xa = mix(g.a, g.b, 0.3), xf = mix(g.a, g.b, 0.58), kc = Math.min(n - 1, Math.max(0, Math.round(n * 0.14)));
      pin(parts.indexOf('cover'), xa, topY(xa) - 17, xa, g.top + sag(xa) + g.quilt * 0.5, show);
      pin(parts.indexOf('foam'), xf, g.top + sag(xf) + g.quilt + g.foam * 0.52, null, null, show);
      pin(parts.indexOf('coil'), cols[kc].x, g.coilTop + Math.max(g.gap, cols[kc].y) + (g.tall - g.gap) * 0.5, null, null, show);
      pin(parts.indexOf('edge'), g.x1 + 18, g.coilTop + g.tall * 0.42, g.edge + 4, g.coilTop + g.tall * 0.42, show);
      pin(parts.indexOf('vent'), g.x0 - 18, vy, vents[0] - 4.5, vy, show);
      pin(parts.indexOf('label'), lx + lw + 3, ly - 3, null, null, show);
    }

    function demoX() { return mix(G.a, G.b, 0.62); }
    function frame(now) {
      var dt = Math.min(0.034, (now - last) / 1000 || 0.016); last = now;
      if (!G) { raf = 0; return; }
      if (introAt) {
        var t = (now - introAt) / 1000;
        peel = clamp((t - 0.35) / 1.5); sign = clamp((t - 1.75) / 0.8);
        if (!touched) {
          if (demo === 0 && t > 2.35) { demo = 1; px = pxTo = demoX(); loadTo = HOVER; }
          else if (demo === 1 && t > 2.8) { demo = 2; loadTo = PRESS; }
          else if (demo === 2 && t > 3.85) { demo = 3; loadTo = 0; }
        }
        if (t > 3.9 || (touched && t > 2.6)) introAt = 0;
      }
      px = mix(px, pxTo, 0.2); load = mix(load, loadTo, 0.16);
      step(dt / 2); step(dt / 2);
      try { draw(); } catch (err) { raf = 0; return; }
      var moving = !!introAt || Math.abs(load - loadTo) > 2 || Math.abs(px - pxTo) > 0.4, i;
      for (i = 0; i < cols.length && !moving; i++) if (Math.abs(cols[i].v) > 0.6 || (loadTo === 0 && Math.abs(cols[i].y) > 0.15)) moving = true;
      raf = onScreen && moving ? requestAnimationFrame(frame) : 0;
    }
    function wake() { if (!raf && onScreen && !still) { last = performance.now(); raf = requestAnimationFrame(frame); } }
    function aim(e) {
      if (!G) return;
      var box = canvas.getBoundingClientRect(), x = e.clientX - box.left, y = e.clientY - box.top;
      inside = peel > 0.98 && x > G.cut - 6 && x < G.x1 + 10 && y > G.top - 44 && y < G.floor + 6;
      pxTo = Math.max(G.a, Math.min(G.b, x));
      if (load < 40) px = pxTo;
      touched = true;
      if (loadTo !== PRESS) loadTo = inside ? HOVER : 0;
      wake();
    }
    function letGo() { loadTo = 0; wake(); }
    function repaint() {
      readColors(); layout();
      if (!G) return;
      if (still) {
        px = pxTo = demoX(); load = loadTo = PRESS;
        for (var i = 0; i < 400; i++) step(1 / 120);
        draw();
      } else { draw(); wake(); }
    }

    repaint();
    partEls.forEach(function (el, i) {
      el.addEventListener('pointerenter', function () { hot = i; if (!raf) draw(); });
      el.addEventListener('pointerleave', function () { hot = -1; if (!raf) draw(); });
    });
    if (!still) {
      stage.addEventListener('pointermove', aim);
      canvas.addEventListener('pointerdown', function (e) { aim(e); if (inside) { loadTo = PRESS; wake(); } });
      stage.addEventListener('pointerup', function (e) { loadTo = e.pointerType === 'mouse' && inside ? HOVER : 0; wake(); });
      stage.addEventListener('pointerleave', letGo);
      stage.addEventListener('pointercancel', letGo);
      /* the cover peels back the first time the drawing is on screen */
      var begin = function (visible) { onScreen = visible; if (visible && !introAt && demo === 0 && peel === 0) introAt = performance.now(); wake(); };
      if ('IntersectionObserver' in window) new IntersectionObserver(function (es) { begin(es[0].isIntersecting); }, { threshold: 0.2 }).observe(canvas);
      else begin(true);
    }
    return repaint;
  }

  /* ---------- boot ---------- */
  var live = [];
  function boot(scope) {
    readStores();
    all('[data-mo-lineup]', scope).forEach(function (root) {
      if (root.__moLineup) return;
      root.__moLineup = true;
      squash(root);
      var hero = root.querySelector('[data-mo-lineup-hero]');
      var style = hero && hero.getAttribute('data-hero-style');
      /* a brand layer can register its own showpiece (window.MOLineupHeroes[style]) */
      var custom = style && window.MOLineupHeroes && typeof window.MOLineupHeroes[style] === 'function' ? window.MOLineupHeroes[style] : null;
      var repaint = custom ? custom(root) : style === 'cutaway' ? heroCutaway(root) : style && style !== 'grid' ? function () {} : heroGrid(root);
      var lineup = Lineup(root);
      live.push({ root: root, lineup: lineup, repaint: repaint });
    });
  }
  function each(fn) { live = live.filter(function (x) { return document.documentElement.contains(x.root); }); live.forEach(fn); }

  window.MOLineup = { boot: boot };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(document); });
  else boot(document);
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('mo:showroom-change', function () { readStores(); each(function (x) { x.lineup.update(); }); });
  window.addEventListener('resize', function () { each(function (x) { x.lineup.placeCursors(); x.repaint(); }); });
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { each(function (x) { x.lineup.placeCursors(); }); });
  if ('MutationObserver' in window) {
    new MutationObserver(function () { each(function (x) { x.repaint(); }); })
      .observe(document.documentElement, { attributes: true, attributeFilter: ['data-mo-scheme'] });
  }

  /* prices roll to the new value with Number Flow (MIT, Maxwell Barvian), self-hosted in theme assets */
  var tag = document.querySelector('script[data-mo-lineup-flow]');
  var src = tag && tag.getAttribute('data-mo-lineup-flow');
  // Loaded as a module script element. A dynamic import() here is rewritten by Shopify's asset
  // minifier into a CommonJS require() and never runs.
  if (src && !still && window.customElements) {
    var loader = document.createElement('script');
    loader.type = 'module';
    loader.src = src;
    loader.onload = function () {
      if (!customElements.get('number-flow')) return;
      flowReady = true;
      each(function (x) { x.lineup.update(); });
    };
    document.head.appendChild(loader);
  }
})();
